import React, { useState, useEffect } from 'react';
import { roomsApi, guestsApi, reservationsApi, paymentsApi, invoicesApi, billSettingsApi } from '../../services/api';
import { calcBill, calcNights, fmt, genId, PAYMENT_TYPES, BILLING_TYPES, getTodayStr, getNextDayStr, getCurrentTimeStr } from '../../utils/billing';
import { generateInvoicePDF } from '../../utils/pdfGenerator';
import { useNotify, useConfirm } from '../../context/ConfirmContext';

// Same "hh:mm AM/PM" <-> 24h bridge as CheckInPage's arrival-time field —
// duplicated locally rather than shared since it's a tiny, self-contained
// format conversion tied to this one input.
function timeTo24h(time12) {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(time12 || '');
  if (!m) return '';
  let h = parseInt(m[1], 10);
  const ap = m[3].toUpperCase();
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${m[2]}`;
}
function timeFrom24h(time24) {
  const [hStr, min] = (time24 || '').split(':');
  let h = parseInt(hStr, 10);
  if (isNaN(h) || min === undefined) return '';
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12; if (h === 0) h = 12;
  return `${String(h).padStart(2, '0')}:${min} ${ap}`;
}

const mkPayRow = () => ({ id: Date.now() + Math.random(), type: 'cash', amount: '', last5: '', note: '' });

// Shared split-payment editor — used by both "Create" (advance) and
// "Checkout" (final settlement). Rows must sum to `totalDue`.
function SplitPaymentEditor({ rows, setRows, totalDue, notify }) {
  return (
    <>
      {rows.map((p, idx) => {
        const isNonCash = ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(p.type);
        const otherTotal = rows.filter(r => r.id !== p.id).reduce((s, r) => s + (Number(r.amount) || 0), 0);
        const remaining = Math.max(0, totalDue - otherTotal);
        return (
          <div key={p.id} style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>💳 Payment {idx + 1}</span>
              {rows.length > 1 && (
                <button type="button" onClick={() => setRows(prev => prev.filter(r => r.id !== p.id))}
                  style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}>✕ Remove</button>
              )}
            </div>
            <div className="pay-type-grid">
              {PAYMENT_TYPES.map(pt => (
                <button key={pt.value} className={`pay-type-btn ${p.type === pt.value ? 'selected' : ''}`} onClick={() => setRows(prev => prev.map(r => r.id === p.id ? { ...r, type: pt.value, last5: '' } : r))}>
                  <span className="pay-type-icon">{pt.icon}</span>
                  <span className="pay-type-label">{pt.label}</span>
                </button>
              ))}
            </div>
            <div className="form-row-2" style={{ marginTop: 12 }}>
              <div className="field">
                <label>Amount (₹) <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>Remaining: {fmt(remaining)}</span></label>
                <input type="number" value={p.amount} onChange={e => {
                  const val = Number(e.target.value) || 0;
                  if (val > remaining + 0.01) return;
                  setRows(prev => prev.map(r => r.id === p.id ? { ...r, amount: e.target.value } : r));
                }} />
              </div>
              {isNonCash && (
                <div className="field">
                  <label>Reference (Last 5) *</label>
                  <input value={p.last5} onChange={e => setRows(prev => prev.map(r => r.id === p.id ? { ...r, last5: e.target.value.slice(-5) } : r))} placeholder="e.g. 43210" maxLength={5} />
                </div>
              )}
            </div>
            <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
              <label>Remarks <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(optional)</span></label>
              <input value={p.note} onChange={e => setRows(prev => prev.map(r => r.id === p.id ? { ...r, note: e.target.value } : r))} placeholder="Reference / note" />
            </div>
          </div>
        );
      })}
      {(() => {
        if (totalDue <= 0) return null;
        const splitTotal = rows.reduce((s, p) => s + (Number(p.amount) || 0), 0);
        const diff = totalDue - splitTotal;
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, marginBottom: 10, background: Math.abs(diff) < 0.5 ? 'rgba(109,191,148,0.12)' : 'rgba(232,184,109,0.10)' }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>{Math.abs(diff) < 0.5 ? '✅ Fully allocated' : `⚠️ ${fmt(diff)} not yet allocated`}</span>
            {diff > 0.5 && (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setRows(prev => [...prev, { ...mkPayRow(), amount: String(diff) }])}>+ Add another payment method</button>
            )}
          </div>
        );
      })()}
    </>
  );
}

export default function BulkBookingPage({ hotel, onSuccess }) {
  const [pageMode, setPageMode] = useState('create'); // 'create' | 'checkin' | 'checkout'

  if (!hotel) return <div className="empty"><div className="empty-icon">👥</div>Select a property first</div>;

  const TABS = [
    { value: 'create', label: '➕ New Bulk Booking' },
    { value: 'checkin', label: '🛎️ Bulk Check-In' },
    { value: 'checkout', label: '🚪 Bulk Checkout' },
    { value: 'rate', label: '✏️ Edit Group' },
  ];

  return (
    <div>
      <div className="panel" style={{ marginBottom: 20 }}>
        <div style={{ padding: '14px 18px', display: 'flex', gap: 10 }}>
          {TABS.map(t => (
            <button key={t.value} className={`btn ${pageMode === t.value ? 'btn-gold' : 'btn-ghost'}`}
              style={{ flex: 1, border: pageMode === t.value ? 'none' : '1px solid var(--border2)' }}
              onClick={() => setPageMode(t.value)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {pageMode === 'create' && <CreateBulkGroup hotel={hotel} onSuccess={onSuccess} />}
      {pageMode === 'checkin' && <BulkCheckIn hotel={hotel} onSuccess={onSuccess} />}
      {pageMode === 'checkout' && <BulkCheckout hotel={hotel} onSuccess={onSuccess} />}
      {pageMode === 'rate' && <BulkEditRate hotel={hotel} onSuccess={onSuccess} />}
    </div>
  );
}

// ─── MODE 1: CREATE A NEW BULK/GROUP BOOKING ────────────────────────────────
function CreateBulkGroup({ hotel, onSuccess }) {
  const notify = useNotify();
  const [bookingStatus, setBookingStatus] = useState('confirmed'); // 'confirmed' (reserve) | 'checked-in' (guests here now)
  const [allRooms, setAllRooms] = useState([]);
  const [loading, setLoading] = useState(false);

  const [guest, setGuest] = useState({ name: '', phone: '' });
  const [checkIn, setCheckIn] = useState(getTodayStr());
  const [checkInTime, setCheckInTime] = useState(getCurrentTimeStr());
  const [checkOut, setCheckOut] = useState(getNextDayStr(getTodayStr()));
  const [checkOutTime, setCheckOutTime] = useState('12:00 PM');
  const [roomCount, setRoomCount] = useState('1');
  const [offeredPrice, setOfferedPrice] = useState('');
  const [priceIncludesGst, setPriceIncludesGst] = useState(false);
  const [inclusivePrice, setInclusivePrice] = useState('');
  const [billingType, setBillingType] = useState('non-gst');
  const [notes, setNotes] = useState('');
  const [advancePaid, setAdvancePaid] = useState('0');
  const [payments, setPayments] = useState([mkPayRow()]);

  useEffect(() => {
    if (!hotel || bookingStatus !== 'checked-in') return;
    roomsApi.list(hotel.id).then(setAllRooms).catch(() => setAllRooms([]));
  }, [hotel, bookingStatus]);

  // Keep the single-row (common) case effortless, same pattern used
  // elsewhere: as long as the payment hasn't been split, its one row
  // mirrors the advance total above it.
  useEffect(() => {
    setPayments(prev => prev.length === 1 ? [{ ...prev[0], amount: advancePaid }] : prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancePaid]);

  const gstRate = Number(hotel?.gstRate ?? 5);
  const nights = calcNights(checkIn, checkOut) || 1;
  const count = Math.max(0, parseInt(roomCount, 10) || 0);
  const totalVacant = allRooms.filter(r => r.status === 'vacant').length;

  const effectiveRate = priceIncludesGst
    ? (gstRate > 0 ? Math.round((Number(inclusivePrice || 0) / (1 + gstRate / 100)) * 100) / 100 : Number(inclusivePrice || 0))
    : Number(offeredPrice) || 0;

  const perRoomBill = calcBill({ roomRate: effectiveRate, nights, extraCharges: 0, damageCharges: 0 }, billingType, gstRate);
  const grandTotal = perRoomBill.total * count;

  const resetForm = () => {
    setGuest({ name: '', phone: '' });
    setCheckIn(getTodayStr());
    setCheckInTime(getCurrentTimeStr());
    setCheckOut(getNextDayStr(getTodayStr()));
    setCheckOutTime('12:00 PM');
    setRoomCount('1');
    setOfferedPrice('');
    setPriceIncludesGst(false);
    setInclusivePrice('');
    setNotes('');
    setAdvancePaid('0');
    setPayments([mkPayRow()]);
  };

  const handleSubmit = async () => {
    if (!guest.name || !guest.phone) { await notify('Enter the group contact\'s name and phone.'); return; }
    if (count < 1) { await notify('Enter how many rooms to book.'); return; }
    if (!checkIn || !checkOut || checkOut <= checkIn) { await notify('Check dates in and out.'); return; }
    if (effectiveRate <= 0) { await notify('Enter a rate per room.'); return; }
    if (bookingStatus === 'checked-in' && count > totalVacant) {
      await notify(`Only ${totalVacant} vacant room(s) available right now — reduce the room count or free up more rooms first.`);
      return;
    }
    const advanceTotal = Number(advancePaid) || 0;
    const activePayments = payments.filter(p => Number(p.amount) > 0);
    if (advanceTotal > 0) {
      const splitTotal = activePayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
      if (Math.abs(advanceTotal - splitTotal) > 0.5) {
        await notify(`Payment split (${fmt(splitTotal)}) doesn't match the advance amount (${fmt(advanceTotal)}).`);
        return;
      }
      const missingRef = activePayments.find(p => ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(p.type) && !p.last5);
      if (missingRef) { await notify('Enter the reference (last 5 digits) for every non-cash payment.'); return; }
    }

    setLoading(true);
    try {
      // Resolve the group contact guest record — reuse an exact phone match,
      // otherwise create a fresh one. Individual per-room guest/ID details
      // can still be filled in later via Edit Reservation for whichever
      // rooms need it.
      let guestId;
      const matches = await guestsApi.list(guest.phone);
      const existing = matches.find(g => g.phone === guest.phone);
      if (existing) {
        guestId = existing.id;
      } else {
        const created = await guestsApi.create({ name: guest.name, phone: guest.phone, hotelId: hotel.id });
        guestId = created.id;
      }

      // Any vacant room, any type — auto-assigned in room-number order.
      const assignedRooms = bookingStatus === 'checked-in'
        ? allRooms.filter(r => r.status === 'vacant').sort((a, b) => (a.number || '').localeCompare(b.number || '', 'en', { numeric: true })).slice(0, count)
        : [];

      // One id shared by every reservation in this batch — lets Bulk Check-In
      // and Bulk Checkout find "the rest of this group" later.
      const bulkGroupId = genId('BG');

      // Split the advance evenly across the N rooms, and mirror each
      // payment row's method proportionally so every room's own balance
      // (and Cash Management's totals) stay sensible when checked out
      // individually later — not all dumped onto a single room.
      const created = [];
      for (let i = 0; i < count; i++) {
        const perRoomAdvance = advanceTotal > 0 ? Math.round((advanceTotal / count) * 100) / 100 : 0;
        const advancePayments = activePayments.map(p => ({
          type: p.type,
          amount: Math.round((Number(p.amount) / count) * 100) / 100,
          last5: p.last5 || null,
          note: p.note || `Bulk booking (${count} rooms)`
        })).filter(p => p.amount > 0);

        // check_out_time in the reservations table records the guest's REAL
        // departure, set later at actual checkout — it can't double as a
        // planned/scheduled time captured here, so the planned window goes
        // into notes instead, where it's still visible to staff.
        const scheduleNote = `Scheduled: ${checkInTime} → ${checkOutTime}`;
        const payload = {
          id: genId('R'), guestId, hotelId: hotel.id,
          room: bookingStatus === 'checked-in' ? assignedRooms[i]?.number : undefined,
          roomType: bookingStatus === 'checked-in' ? assignedRooms[i]?.room_type : undefined,
          checkIn, checkOut,
          checkInTime,
          status: bookingStatus, nights,
          roomRate: effectiveRate,
          extraCharges: 0,
          totalAmount: perRoomBill.total,
          advancePaid: perRoomAdvance,
          advancePayments,
          billingType,
          customerType: 'individual',
          bulkGroupId,
          notes: notes ? `${notes}\n${scheduleNote}` : `Bulk/group booking — ${count} rooms. ${scheduleNote}`,
        };
        created.push(await reservationsApi.create(payload));
      }

      onSuccess?.(`${count} reservation${count > 1 ? 's' : ''} created for ${guest.name}.`, 'success');
      resetForm();
    } catch (err) {
      onSuccess?.('Error: ' + err.message, 'error');
    }
    setLoading(false);
  };

  return (
    <div>
      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">👥 Bulk / Group Booking</span>
          <span className="panel-sub">One room type, one rate, applied to every room at once</span>
        </div>
        <div style={{ padding: '20px 24px' }}>

          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            <label style={{ flex: 1, padding: '14px 16px', borderRadius: 10, border: `2px solid ${bookingStatus === 'checked-in' ? 'var(--accent)' : 'var(--border)'}`, background: bookingStatus === 'checked-in' ? 'var(--s1)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="radio" checked={bookingStatus === 'checked-in'} onChange={() => setBookingStatus('checked-in')} style={{ width: 16, height: 16 }} />
              <div><div style={{ fontWeight: 700, fontSize: 13 }}>🛎️ Check In Now</div><div style={{ fontSize: 11, color: 'var(--muted)' }}>Group has arrived — rooms auto-assigned from vacant stock</div></div>
            </label>
            <label style={{ flex: 1, padding: '14px 16px', borderRadius: 10, border: `2px solid ${bookingStatus === 'confirmed' ? 'var(--accent)' : 'var(--border)'}`, background: bookingStatus === 'confirmed' ? 'var(--s1)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="radio" checked={bookingStatus === 'confirmed'} onChange={() => setBookingStatus('confirmed')} style={{ width: 16, height: 16 }} />
              <div><div style={{ fontWeight: 700, fontSize: 13 }}>📅 Reserve For Later</div><div style={{ fontSize: 11, color: 'var(--muted)' }}>Advance booking — specific rooms assigned later via Bulk Check-In</div></div>
            </label>
          </div>

          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', marginBottom: 12 }}>Group Contact</div>
          <div className="form-row-2" style={{ marginBottom: 20 }}>
            <div className="field"><label>Name *</label><input value={guest.name} onChange={e => setGuest(g => ({ ...g, name: e.target.value }))} placeholder="Group leader / booking agent" /></div>
            <div className="field"><label>Phone *</label><input value={guest.phone} onChange={e => setGuest(g => ({ ...g, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="10-digit mobile" /></div>
          </div>

          <div className="form-row-2" style={{ marginBottom: 8 }}>
            <div className="field"><label>Check-in Date *</label><input type="date" min={getTodayStr()} value={checkIn} onChange={e => setCheckIn(e.target.value)} /></div>
            <div className="field"><label>Check-out Date *</label><input type="date" min={checkIn} value={checkOut} onChange={e => setCheckOut(e.target.value)} /></div>
          </div>
          <div className="form-row-2" style={{ marginBottom: 20 }}>
            <div className="field"><label>Arrival Time {bookingStatus === 'checked-in' ? '*' : '(planned)'}</label><input type="time" value={timeTo24h(checkInTime)} onChange={e => setCheckInTime(timeFrom24h(e.target.value) || checkInTime)} /></div>
            <div className="field"><label>Departure Time (planned)</label><input type="time" value={timeTo24h(checkOutTime)} onChange={e => setCheckOutTime(timeFrom24h(e.target.value) || checkOutTime)} /></div>
          </div>

          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', marginBottom: 12 }}>Rooms — same rate applied to every room, any type</div>
          <div className="field" style={{ marginBottom: 8 }}>
            <label>Room Count *</label>
            <input type="number" min="1" value={roomCount} onChange={e => setRoomCount(e.target.value)} />
            {bookingStatus === 'checked-in' && (
              <div style={{ fontSize: 10, color: totalVacant < count ? 'var(--danger)' : 'var(--muted)', marginTop: 4 }}>
                {totalVacant} vacant room(s) right now (any type) — rooms are auto-assigned
              </div>
            )}
          </div>

          <div className="field" style={{ marginBottom: 8 }}>
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{priceIncludesGst ? 'Rate / Room, GST-incl. (₹) *' : 'Rate / Room (₹) *'}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 400, color: 'var(--muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={priceIncludesGst} onChange={e => {
                  const checked = e.target.checked;
                  if (checked) {
                    const inclusiveVal = Number(offeredPrice) || 0;
                    const exclusive = gstRate > 0 ? Math.round((inclusiveVal / (1 + gstRate / 100)) * 100) / 100 : inclusiveVal;
                    setInclusivePrice(String(inclusiveVal));
                    setOfferedPrice(String(exclusive));
                    setPriceIncludesGst(true);
                    setBillingType('gst');
                  } else {
                    setPriceIncludesGst(false);
                  }
                }} />
                Includes GST
              </span>
            </label>
            {priceIncludesGst ? (
              <input type="number" value={inclusivePrice} onChange={e => {
                const inclusiveVal = Number(e.target.value) || 0;
                const exclusive = gstRate > 0 ? Math.round((inclusiveVal / (1 + gstRate / 100)) * 100) / 100 : inclusiveVal;
                setInclusivePrice(e.target.value);
                setOfferedPrice(String(exclusive));
              }} placeholder="e.g. 2200" style={{ fontWeight: 700, color: 'var(--teal)', border: '1px solid var(--teal)', outline: 'none' }} />
            ) : (
              <input type="number" value={offeredPrice} onChange={e => setOfferedPrice(e.target.value)} style={{ fontWeight: 700, color: 'var(--teal)', border: '1px solid var(--teal)', outline: 'none' }} />
            )}
            {priceIncludesGst && (
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
                = {fmt(offeredPrice)} room + {fmt((Number(inclusivePrice) || 0) - (Number(offeredPrice) || 0))} GST @ {gstRate}%
              </div>
            )}
          </div>

          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Billing Configuration</div>
            <div style={{ display: 'flex', gap: 12 }}>
              {BILLING_TYPES.map(bt => (
                <label key={bt.value} style={{ flex: 1, padding: '12px 14px', borderRadius: 8, border: `2px solid ${billingType === bt.value ? 'var(--accent)' : 'var(--border)'}`, background: billingType === bt.value ? 'var(--s1)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="radio" checked={billingType === bt.value} onChange={() => setBillingType(bt.value)} style={{ width: 16, height: 16 }} />
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{bt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="field" style={{ marginBottom: 20 }}>
            <label>Notes</label>
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Sharma Wedding Party" />
          </div>

          <div className="bill-box" style={{ marginBottom: 24 }}>
            <div className="bill-row"><span>Per Room ({nights} night{nights > 1 ? 's' : ''}{billingType === 'gst' ? ` + GST @ ${gstRate}%` : ''})</span><span>{fmt(perRoomBill.total)}</span></div>
            <div className="bill-row bill-total"><span>Grand Total ({count} room{count !== 1 ? 's' : ''})</span><span>{fmt(grandTotal)}</span></div>
          </div>

          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', marginBottom: 12 }}>Advance Payment</div>
          <div className="field" style={{ marginBottom: 16 }}>
            <label>Total Advance Received (₹)</label>
            <input type="number" min="0" value={advancePaid} onChange={e => {
              const val = Number(e.target.value) || 0;
              if (val > grandTotal) { notify(`Advance cannot exceed the grand total of ${fmt(grandTotal)}`); return; }
              setAdvancePaid(e.target.value);
            }} />
          </div>

          {Number(advancePaid) > 0 && (
            <SplitPaymentEditor rows={payments} setRows={setPayments} totalDue={Number(advancePaid) || 0} notify={notify} />
          )}

          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 16 }}>
            The advance is split evenly across all {count || 0} room{count !== 1 ? 's' : ''} so each has a sensible balance if checked out individually later. Individual guest names/ID can be added per room via Edit Reservation afterward.
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-gold btn-lg" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Creating...' : `Create ${count || 0} Reservation${count !== 1 ? 's' : ''} →`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MODE 2: BULK CHECK-IN AN ALREADY-BOOKED GROUP ──────────────────────────
function BulkCheckIn({ hotel, onSuccess }) {
  const notify = useNotify();
  const [phoneFilter, setPhoneFilter] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [allGroups, setAllGroups] = useState([]); // every pending group, unfiltered
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [checkInCount, setCheckInCount] = useState('0'); // how many of the group's pending rooms are arriving now
  const [extraRooms, setExtraRooms] = useState('0');
  const [roomNumbersInput, setRoomNumbersInput] = useState('');
  const [checkInTimeNow, setCheckInTimeNow] = useState(getCurrentTimeStr());
  const [allRooms, setAllRooms] = useState([]);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!hotel) return;
    roomsApi.list(hotel.id).then(setAllRooms).catch(() => setAllRooms([]));
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel]);

  // Every pending (unassigned) bulk group for this hotel — no phone needed to
  // browse; the field below just narrows this list further.
  const loadGroups = async () => {
    setLoadingList(true);
    setSelectedGroup(null);
    try {
      const reservations = await reservationsApi.list(hotel.id, 'confirmed');
      const mine = reservations.filter(r => r.bulk_group_id && !r.room);
      const byGroup = new Map();
      mine.forEach(r => {
        if (!byGroup.has(r.bulk_group_id)) byGroup.set(r.bulk_group_id, []);
        byGroup.get(r.bulk_group_id).push(r);
      });
      const groups = Array.from(byGroup.entries()).map(([groupId, rows]) => ({
        groupId, guestId: rows[0].guest_id, guestName: rows[0].guest_name, guestPhone: rows[0].guest_phone,
        reservations: rows,
        roomCount: rows.length,
        checkIn: rows[0].check_in, checkOut: rows[0].check_out,
        nights: rows[0].nights, roomRate: Number(rows[0].room_rate),
        billingType: rows[0].billing_type,
      }));
      setAllGroups(groups);
    } catch (err) {
      await notify(err.message);
      setAllGroups([]);
    }
    setLoadingList(false);
  };

  const cleanFilter = phoneFilter.replace(/\D/g, '');
  const visibleGroups = cleanFilter
    ? allGroups.filter(g => (g.guestPhone || '').includes(cleanFilter))
    : allGroups;

  const selectGroup = (g) => {
    setSelectedGroup(g);
    setCheckInCount(String(g.roomCount)); // default to the whole group; editable down for a partial arrival
    setExtraRooms('0');
    setRoomNumbersInput('');
  };

  const checkInN = selectedGroup ? Math.min(selectedGroup.roomCount, Math.max(0, parseInt(checkInCount, 10) || 0)) : 0;
  const totalNeeded = selectedGroup ? checkInN + (Math.max(0, parseInt(extraRooms, 10) || 0)) : 0;
  const enteredRooms = roomNumbersInput.split(',').map(s => s.trim()).filter(Boolean);
  const vacantNumbers = new Set(allRooms.filter(r => r.status === 'vacant').map(r => r.number));
  const roomValidation = enteredRooms.map(num => ({
    number: num,
    exists: allRooms.some(r => r.number === num),
    vacant: vacantNumbers.has(num),
    duplicate: enteredRooms.filter(n => n === num).length > 1
  }));
  const allRoomsValid = totalNeeded > 0 && enteredRooms.length === totalNeeded && roomValidation.every(r => r.exists && r.vacant && !r.duplicate);

  const handleConfirm = async () => {
    if (!selectedGroup) return;
    if (!allRoomsValid) { await notify(`Enter exactly ${totalNeeded} valid, vacant, unique room numbers.`); return; }
    setProcessing(true);
    try {
      // Only the rooms actually arriving now — the rest of the group's
      // unassigned reservations are left untouched, still pending, for a
      // later Bulk Check-In when the remaining guests show up.
      for (let i = 0; i < checkInN; i++) {
        const roomObj = allRooms.find(r => r.number === enteredRooms[i]);
        await reservationsApi.update(selectedGroup.reservations[i].id, {
          room: enteredRooms[i], roomType: roomObj?.room_type,
          status: 'checked-in', checkInTime: checkInTimeNow
        });
      }
      // Adhoc extra rooms the group asked for on the spot — new reservations,
      // same rate/dates/billing as the rest of the group, same group id.
      const extraCount = totalNeeded - checkInN;
      const perRoomBill = calcBill({ roomRate: selectedGroup.roomRate, nights: selectedGroup.nights, extraCharges: 0, damageCharges: 0 }, selectedGroup.billingType, Number(hotel?.gstRate ?? 5));
      for (let i = 0; i < extraCount; i++) {
        const num = enteredRooms[checkInN + i];
        const roomObj = allRooms.find(r => r.number === num);
        await reservationsApi.create({
          id: genId('R'), guestId: selectedGroup.guestId, hotelId: hotel.id,
          room: num, roomType: roomObj?.room_type,
          checkIn: selectedGroup.checkIn, checkOut: selectedGroup.checkOut,
          checkInTime: checkInTimeNow, status: 'checked-in', nights: selectedGroup.nights,
          roomRate: selectedGroup.roomRate, extraCharges: 0, totalAmount: perRoomBill.total,
          advancePaid: 0, billingType: selectedGroup.billingType, customerType: 'individual',
          bulkGroupId: selectedGroup.groupId,
          notes: `Added on the spot at check-in — same group rate as the rest of ${selectedGroup.guestName}'s booking.`
        });
      }
      const remaining = selectedGroup.roomCount - checkInN;
      onSuccess?.(`Checked in ${totalNeeded} room(s) for ${selectedGroup.guestName}.${remaining > 0 ? ` ${remaining} room(s) still pending for later.` : ''}`, 'success');
      setRoomNumbersInput(''); setExtraRooms('0'); setCheckInCount('0');
      loadGroups();
    } catch (err) {
      onSuccess?.('Error: ' + err.message, 'error');
    }
    setProcessing(false);
  };

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">🛎️ Bulk Check-In</span>
        <span className="panel-sub">Find a previously-booked group and assign all its rooms at once</span>
      </div>
      <div style={{ padding: '20px 24px' }}>
        {!selectedGroup && (
          <>
            <div className="field" style={{ marginBottom: 16 }}>
              <label>Filter by Group Contact's Phone <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(optional)</span></label>
              <input value={phoneFilter} onChange={e => setPhoneFilter(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Leave blank to see every pending group" />
            </div>

            {loadingList && <div style={{ padding: '16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Loading...</div>}

            {!loadingList && visibleGroups.length === 0 && (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
                {cleanFilter ? 'No pending bulk bookings found for this number.' : 'No bulk bookings awaiting check-in right now.'}
              </div>
            )}

            {!loadingList && visibleGroups.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                {visibleGroups.map(g => (
                  <div key={g.groupId} onClick={() => selectGroup(g)} style={{ cursor: 'pointer', background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{g.guestName} · {g.roomCount} room(s)</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{g.guestPhone} · {g.checkIn} → {g.checkOut} · {fmt(g.roomRate)}/night each</div>
                    </div>
                    <button className="btn btn-sm btn-gold">Select →</button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {selectedGroup && (
          <div>
            <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{selectedGroup.guestName}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{selectedGroup.checkIn} → {selectedGroup.checkOut} · {selectedGroup.roomCount} room(s) booked at {fmt(selectedGroup.roomRate)}/night</div>
              <button className="btn btn-sm btn-ghost" style={{ marginTop: 8 }} onClick={() => setSelectedGroup(null)}>← Choose a different group</button>
            </div>

            <div className="form-row-2" style={{ marginBottom: 16 }}>
              <div className="field">
                <label>Arrival Time *</label>
                <input type="time" value={timeTo24h(checkInTimeNow)} onChange={e => setCheckInTimeNow(timeFrom24h(e.target.value) || checkInTimeNow)} />
              </div>
              <div className="field">
                <label>Rooms Checking In Now (of {selectedGroup.roomCount} booked)</label>
                <input type="number" min="1" max={selectedGroup.roomCount} value={checkInCount} onChange={e => setCheckInCount(e.target.value)} />
                {checkInN < selectedGroup.roomCount && (
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>The remaining {selectedGroup.roomCount - checkInN} room(s) stay pending for a later check-in.</div>
                )}
              </div>
            </div>

            <div className="field" style={{ marginBottom: 16 }}>
              <label>Additional Rooms Needed On The Spot</label>
              <input type="number" min="0" value={extraRooms} onChange={e => setExtraRooms(e.target.value)} />
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Billed at the same {fmt(selectedGroup.roomRate)}/night group rate.</div>
            </div>

            <div className="field" style={{ marginBottom: 8 }}>
              <label>Room Numbers, comma-separated ({totalNeeded} needed) *</label>
              <textarea rows={2} value={roomNumbersInput} onChange={e => setRoomNumbersInput(e.target.value)} placeholder="e.g. 301,302,303,304,305,306,307,308,309,310" style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border2)', background: 'var(--s2)', fontFamily: 'var(--font-b)', fontSize: 13 }} />
            </div>

            {enteredRooms.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                {roomValidation.map(r => (
                  <span key={r.number} style={{
                    fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 12,
                    background: (r.exists && r.vacant && !r.duplicate) ? 'rgba(109,191,148,0.15)' : 'rgba(220,80,80,0.12)',
                    color: (r.exists && r.vacant && !r.duplicate) ? 'var(--success)' : 'var(--danger)'
                  }}>
                    {r.number} {!r.exists ? '(unknown)' : r.duplicate ? '(dup)' : !r.vacant ? '(not vacant)' : '✓'}
                  </span>
                ))}
                {enteredRooms.length !== totalNeeded && (
                  <span style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 700 }}>Need {totalNeeded}, entered {enteredRooms.length}</span>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-gold btn-lg" onClick={handleConfirm} disabled={processing || !allRoomsValid}>
                {processing ? 'Checking In...' : `Check In ${totalNeeded} Room(s) →`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MODE 3: BULK CHECKOUT — ONE COMBINED BILL FOR THE WHOLE GROUP ──────────
function BulkCheckout({ hotel, onSuccess }) {
  const notify = useNotify();
  const [phoneFilter, setPhoneFilter] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [allGroups, setAllGroups] = useState([]); // every active (checked-in) group, unfiltered
  const [selectedGroup, setSelectedGroup] = useState(null); // { groupId, guestId, guestName, guestPhone, reservations, paidSoFar, grandTotal, ... }
  const [checkOutTimeNow, setCheckOutTimeNow] = useState(getCurrentTimeStr());
  const [payments, setPayments] = useState([mkPayRow()]);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!hotel) return;
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel]);

  const loadGroups = async () => {
    setLoadingList(true);
    setSelectedGroup(null);
    try {
      const reservations = await reservationsApi.list(hotel.id, 'checked-in');
      const mine = reservations.filter(r => r.bulk_group_id);
      const byGroup = new Map();
      mine.forEach(r => {
        if (!byGroup.has(r.bulk_group_id)) byGroup.set(r.bulk_group_id, []);
        byGroup.get(r.bulk_group_id).push(r);
      });
      const groups = Array.from(byGroup.entries()).map(([groupId, rows]) => ({
        groupId, guestId: rows[0].guest_id, guestName: rows[0].guest_name, guestPhone: rows[0].guest_phone,
        reservations: rows,
      }));
      setAllGroups(groups);
    } catch (err) {
      await notify(err.message);
      setAllGroups([]);
    }
    setLoadingList(false);
  };

  const cleanFilter = phoneFilter.replace(/\D/g, '');
  const visibleGroups = cleanFilter
    ? allGroups.filter(g => (g.guestPhone || '').includes(cleanFilter))
    : allGroups;

  const selectGroup = async (g) => {
    setProcessing(true);
    try {
      const gstRate = Number(hotel?.gstRate ?? 5);
      const paymentsByRes = await Promise.all(g.reservations.map(r => paymentsApi.forReservation(r.id).catch(() => [])));
      const allPayments = paymentsByRes.flat();
      const paidSoFar = allPayments.reduce((s, p) => s + Number(p.amount), 0);

      const roomBills = g.reservations.map(r => {
        const bill = calcBill({ roomRate: Number(r.room_rate), nights: Number(r.nights) || 1, extraCharges: Number(r.extra_charges) || 0, damageCharges: Number(r.damage_charges) || 0 }, r.billing_type, gstRate);
        return { reservation: r, bill };
      });
      const grandTotal = roomBills.reduce((s, rb) => s + rb.bill.total, 0);
      const grandGst = roomBills.reduce((s, rb) => s + rb.bill.gstAmount, 0);
      const grandTaxable = roomBills.reduce((s, rb) => s + rb.bill.taxableStay, 0);
      const balanceDue = Math.max(0, Math.round((grandTotal - paidSoFar) * 100) / 100);

      setSelectedGroup({ ...g, roomBills, allPayments, paidSoFar, grandTotal, grandGst, grandTaxable, balanceDue });
      setPayments([{ ...mkPayRow(), amount: String(balanceDue) }]);
    } catch (err) {
      await notify(err.message);
    }
    setProcessing(false);
  };

  const handleConfirm = async () => {
    if (!selectedGroup) return;
    const activePayments = payments.filter(p => Number(p.amount) > 0);
    const splitTotal = activePayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    if (selectedGroup.balanceDue > 0.5 && Math.abs(splitTotal - selectedGroup.balanceDue) > 0.5) {
      await notify(`Payment split (${fmt(splitTotal)}) doesn't match the balance due (${fmt(selectedGroup.balanceDue)}).`);
      return;
    }
    const missingRef = activePayments.find(p => ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(p.type) && !p.last5);
    if (missingRef) { await notify('Enter the reference (last 5 digits) for every non-cash payment.'); return; }

    setProcessing(true);
    try {
      const primaryResId = selectedGroup.reservations[0].id;

      // Record the group's combined settlement once, against the primary
      // reservation — not per room, since this is one bill for everyone.
      for (const p of activePayments) {
        await paymentsApi.create({
          reservationId: primaryResId, hotelId: hotel.id,
          type: p.type, amount: Number(p.amount) || 0,
          last5: ['upi', 'credit_card', 'debit_card', 'cheque', 'bank_transfer'].includes(p.type) ? p.last5 : null,
          note: p.note || `Bulk group final settlement (${selectedGroup.reservations.length} rooms)`
        });
      }

      // Close out every room in the group. advancePaid is deliberately left
      // untouched here — the real settlement money is the payments just
      // recorded above; each reservation's own advance_paid stays as
      // whatever it already was, same as a normal single checkout.
      for (const r of selectedGroup.reservations) {
        await reservationsApi.update(r.id, { status: 'checked-out', checkOutTime: checkOutTimeNow });
      }

      // One combined invoice, one line item per room.
      const isGst = selectedGroup.roomBills[0]?.reservation.billing_type === 'gst';
      const invoiceItems = selectedGroup.roomBills.map(({ reservation: r, bill }) => ({
        description: `Room Stay Charges (Room ${r.room})`,
        code: '996311',
        details: `${r.nights} night${r.nights > 1 ? 's' : ''} @ ${fmt(r.room_rate)}`,
        amount: bill.taxableStay + (Number(r.extra_charges) || 0)
      }));
      if (isGst && selectedGroup.grandGst > 0) {
        invoiceItems.push({ description: `CGST (${Number(hotel?.gstRate ?? 5) / 2}%)`, code: '-', details: '-', amount: selectedGroup.grandGst / 2 });
        invoiceItems.push({ description: `SGST (${Number(hotel?.gstRate ?? 5) / 2}%)`, code: '-', details: '-', amount: selectedGroup.grandGst / 2 });
      }

      let billDesign = {};
      try { billDesign = await billSettingsApi.get(hotel.id); } catch (_) {}
      const res = await invoicesApi.nextNumber(hotel.id, isGst ? 'gst' : 'non-gst');
      const invoiceNumber = res.invoiceNumber;

      const { pdfOutput, pdfDataUrl } = await generateInvoicePDF({
        reservation: {
          room: selectedGroup.reservations.map(r => r.room).join(', '),
          checkIn: selectedGroup.reservations[0].check_in, checkOut: selectedGroup.reservations[0].check_out,
          nights: selectedGroup.reservations[0].nights,
          guestName: selectedGroup.guestName, guestPhone: selectedGroup.guestPhone,
          billingType: isGst ? 'gst' : 'non-gst'
        },
        guestData: { name: selectedGroup.guestName, phone: selectedGroup.guestPhone },
        hotelData: hotel, billDesign,
        paymentsList: [...selectedGroup.allPayments, ...activePayments],
        invoiceNumber, billingType: isGst ? 'gst' : 'non-gst',
        invoiceItems
      });

      await invoicesApi.create({
        reservationId: primaryResId, guestId: selectedGroup.guestId, hotelId: hotel.id,
        invoiceNumber, totalAmount: selectedGroup.grandTotal, gstAmount: selectedGroup.grandGst,
        billingType: isGst ? 'gst' : 'non-gst', pdfDataUrl, pdfUrl: pdfDataUrl
      });

      const blobUrl = URL.createObjectURL(pdfOutput);
      const win = window.open(blobUrl, '_blank');
      if (!win) {
        const a = document.createElement('a');
        a.href = blobUrl; a.download = `Invoice_${invoiceNumber}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }

      onSuccess?.(`Checked out ${selectedGroup.reservations.length} room(s) for ${selectedGroup.guestName}.`, 'success');
      setPayments([mkPayRow()]);
      loadGroups();
    } catch (err) {
      onSuccess?.('Error: ' + err.message, 'error');
    }
    setProcessing(false);
  };

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">🚪 Bulk Checkout</span>
        <span className="panel-sub">One combined bill and payment for the whole group</span>
      </div>
      <div style={{ padding: '20px 24px' }}>
        {!selectedGroup && (
          <>
            <div className="field" style={{ marginBottom: 16 }}>
              <label>Filter by Group Contact's Phone <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(optional)</span></label>
              <input value={phoneFilter} onChange={e => setPhoneFilter(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Leave blank to see every active group" />
            </div>

            {loadingList && <div style={{ padding: '16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Loading...</div>}

            {!loadingList && visibleGroups.length === 0 && (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
                {cleanFilter ? 'No active bulk groups found for this number.' : 'No active bulk groups checked in right now.'}
              </div>
            )}

            {!loadingList && visibleGroups.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                {visibleGroups.map(g => (
                  <div key={g.groupId} onClick={() => selectGroup(g)} style={{ cursor: processing ? 'default' : 'pointer', background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: processing ? 0.6 : 1 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{g.guestName} · {g.reservations.length} room(s)</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{g.guestPhone} · Rooms: {g.reservations.map(r => r.room).join(', ')}</div>
                    </div>
                    <button className="btn btn-sm btn-gold" disabled={processing}>{processing ? 'Loading...' : 'Select →'}</button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {selectedGroup && (
          <div>
            <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{selectedGroup.guestName}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Rooms: {selectedGroup.reservations.map(r => r.room).join(', ')}</div>
              <button className="btn btn-sm btn-ghost" style={{ marginTop: 8 }} onClick={() => setSelectedGroup(null)}>← Choose a different group</button>
            </div>

            <div className="field" style={{ marginBottom: 16 }}>
              <label>Departure Time *</label>
              <input type="time" value={timeTo24h(checkOutTimeNow)} onChange={e => setCheckOutTimeNow(timeFrom24h(e.target.value) || checkOutTimeNow)} />
            </div>

            <div className="bill-box" style={{ marginBottom: 20 }}>
              {selectedGroup.roomBills.map(({ reservation: r, bill }) => (
                <div className="bill-row" key={r.id}><span>Room {r.room} ({r.nights}n)</span><span>{fmt(bill.total)}</span></div>
              ))}
              <div className="bill-row bill-total"><span>Grand Total</span><span>{fmt(selectedGroup.grandTotal)}</span></div>
              <div className="bill-row"><span>Already Paid</span><span style={{ color: 'var(--success)' }}>- {fmt(selectedGroup.paidSoFar)}</span></div>
              <div className="bill-row bill-due"><span>Balance Due</span><span>{fmt(selectedGroup.balanceDue)}</span></div>
            </div>

            <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 16 }}>
              Doesn't cover per-room damage charges — checkout that one room individually first via the normal Checkout screen if it needs one, then bulk-checkout the rest.
            </div>

            {selectedGroup.balanceDue > 0 && (
              <SplitPaymentEditor rows={payments} setRows={setPayments} totalDue={selectedGroup.balanceDue} notify={notify} />
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn btn-gold btn-lg" onClick={handleConfirm} disabled={processing}>
                {processing ? 'Checking Out...' : `Checkout ${selectedGroup.reservations.length} Room(s) & Print Bill →`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MODE 4: CHANGE A GROUP'S RATE ONCE, APPLIED TO EVERY ROOM IN IT ────────
function BulkEditRate({ hotel, onSuccess }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [phoneFilter, setPhoneFilter] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [allGroups, setAllGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [newRate, setNewRate] = useState('');
  const [processing, setProcessing] = useState(false);
  const [allRooms, setAllRooms] = useState([]);
  const [addCount, setAddCount] = useState('0');
  const [addRoomNumbers, setAddRoomNumbers] = useState('');
  const [newCheckIn, setNewCheckIn] = useState('');
  const [newCheckOut, setNewCheckOut] = useState('');
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editingRoomRate, setEditingRoomRate] = useState('');

  useEffect(() => {
    if (!hotel) return;
    loadGroups();
    roomsApi.list(hotel.id).then(setAllRooms).catch(() => setAllRooms([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel]);

  const fetchAllGroupRows = async () => {
    const [confirmed, checkedIn] = await Promise.all([
      reservationsApi.list(hotel.id, 'confirmed'),
      reservationsApi.list(hotel.id, 'checked-in'),
    ]);
    return [...confirmed, ...checkedIn].filter(r => r.bulk_group_id);
  };

  const buildGroup = (groupId, rows) => {
    const rates = new Set(rows.map(r => Number(r.room_rate)));
    return {
      groupId, guestName: rows[0].guest_name, guestPhone: rows[0].guest_phone,
      reservations: rows, roomCount: rows.length,
      status: rows[0].status,
      rate: rates.size === 1 ? Number(rows[0].room_rate) : null, // null = mixed
      checkIn: rows[0].check_in, checkOut: rows[0].check_out, nights: rows[0].nights,
      billingType: rows[0].billing_type,
    };
  };

  // Both pending and already-checked-in groups can have their rate
  // corrected — a mistake in the negotiated rate is just as likely to
  // surface after the group has arrived as before.
  const loadGroups = async () => {
    setLoadingList(true);
    setSelectedGroup(null);
    try {
      const mine = await fetchAllGroupRows();
      const byGroup = new Map();
      mine.forEach(r => {
        if (!byGroup.has(r.bulk_group_id)) byGroup.set(r.bulk_group_id, []);
        byGroup.get(r.bulk_group_id).push(r);
      });
      setAllGroups(Array.from(byGroup.entries()).map(([groupId, rows]) => buildGroup(groupId, rows)));
    } catch (err) {
      await notify(err.message);
      setAllGroups([]);
    }
    setLoadingList(false);
  };

  // Re-fetches just the selected group in place, so a per-room edit doesn't
  // kick staff back out to the full list.
  const refreshSelectedGroup = async (groupId) => {
    try {
      const mine = await fetchAllGroupRows();
      const rows = mine.filter(r => r.bulk_group_id === groupId);
      if (rows.length === 0) { setSelectedGroup(null); loadGroups(); return; }
      const g = buildGroup(groupId, rows);
      setSelectedGroup(g);
      setAllGroups(prev => prev.map(x => x.groupId === groupId ? g : x));
    } catch (err) {
      await notify(err.message);
    }
  };

  const cleanFilter = phoneFilter.replace(/\D/g, '');
  const visibleGroups = cleanFilter
    ? allGroups.filter(g => (g.guestPhone || '').includes(cleanFilter))
    : allGroups;

  const selectGroup = (g) => {
    setSelectedGroup(g);
    setNewRate(g.rate !== null ? String(g.rate) : '');
    setNewCheckIn(g.checkIn); setNewCheckOut(g.checkOut);
    setAddCount('0'); setAddRoomNumbers('');
    setEditingRoomId(null);
  };

  const handleConfirm = async () => {
    if (!selectedGroup) return;
    const rate = Number(newRate);
    if (!Number.isFinite(rate) || rate <= 0) { await notify('Enter a valid rate.'); return; }
    setProcessing(true);
    try {
      for (const r of selectedGroup.reservations) {
        await reservationsApi.update(r.id, { roomRate: rate });
      }
      onSuccess?.(`Rate updated to ${fmt(rate)}/night across all ${selectedGroup.roomCount} room(s) for ${selectedGroup.guestName}.`, 'success');
      await refreshSelectedGroup(selectedGroup.groupId);
    } catch (err) {
      onSuccess?.('Error: ' + err.message, 'error');
    }
    setProcessing(false);
  };

  const handleUpdateDates = async () => {
    if (!selectedGroup) return;
    if (!newCheckIn || !newCheckOut || newCheckOut <= newCheckIn) { await notify('Check the dates in and out.'); return; }
    setProcessing(true);
    try {
      const nights = calcNights(newCheckIn, newCheckOut) || 1;
      for (const r of selectedGroup.reservations) {
        await reservationsApi.update(r.id, { checkIn: newCheckIn, checkOut: newCheckOut, nights });
      }
      onSuccess?.(`Dates updated across all ${selectedGroup.roomCount} room(s) for ${selectedGroup.guestName}.`, 'success');
      await refreshSelectedGroup(selectedGroup.groupId);
    } catch (err) {
      onSuccess?.('Error: ' + err.message, 'error');
    }
    setProcessing(false);
  };

  const handleSaveRoomRate = async (r) => {
    const rate = Number(editingRoomRate);
    if (!Number.isFinite(rate) || rate <= 0) { await notify('Enter a valid rate.'); return; }
    setProcessing(true);
    try {
      await reservationsApi.update(r.id, { roomRate: rate });
      setEditingRoomId(null);
      onSuccess?.(`Rate updated for Room ${r.room || '(unassigned)'}.`, 'success');
      await refreshSelectedGroup(selectedGroup.groupId);
    } catch (err) {
      onSuccess?.('Error: ' + err.message, 'error');
    }
    setProcessing(false);
  };

  const handleCancelRoom = async (r) => {
    if (r.status === 'checked-in') {
      await notify('This room is occupied — remove it via Checkout or Bulk Checkout instead, so the stay gets billed properly.');
      return;
    }
    const ok = await confirm({
      icon: '🗑️', title: 'Cancel This Room', danger: true, confirmText: 'Cancel Room',
      body: `Cancel this ${fmt(r.room_rate)}/night room booking for ${selectedGroup.guestName}? This can't be undone.`
    });
    if (!ok) return;
    setProcessing(true);
    try {
      await reservationsApi.delete(r.id);
      onSuccess?.('Room cancelled.', 'success');
      if (selectedGroup.roomCount <= 1) { setSelectedGroup(null); loadGroups(); }
      else await refreshSelectedGroup(selectedGroup.groupId);
    } catch (err) {
      onSuccess?.('Error: ' + err.message, 'error');
    }
    setProcessing(false);
  };

  const isCheckedIn = selectedGroup?.status === 'checked-in';
  const addN = Math.max(0, parseInt(addCount, 10) || 0);
  const enteredAddRooms = addRoomNumbers.split(',').map(s => s.trim()).filter(Boolean);
  const vacantNumbers = new Set(allRooms.filter(r => r.status === 'vacant').map(r => r.number));
  const addRoomsValid = !isCheckedIn || (addN > 0 && enteredAddRooms.length === addN &&
    enteredAddRooms.every((num, i) => vacantNumbers.has(num) && enteredAddRooms.indexOf(num) === i));

  const handleAddRooms = async () => {
    if (!selectedGroup || addN < 1) return;
    if (isCheckedIn && !addRoomsValid) { await notify(`Enter exactly ${addN} valid, vacant, unique room numbers.`); return; }
    setProcessing(true);
    try {
      const g = selectedGroup;
      for (let i = 0; i < addN; i++) {
        const roomNum = isCheckedIn ? enteredAddRooms[i] : undefined;
        const roomObj = roomNum ? allRooms.find(r => r.number === roomNum) : null;
        await reservationsApi.create({
          id: genId('R'), guestId: g.reservations[0].guest_id, hotelId: hotel.id,
          room: roomNum, roomType: roomObj?.room_type,
          checkIn: g.reservations[0].check_in, checkOut: g.reservations[0].check_out,
          checkInTime: isCheckedIn ? getCurrentTimeStr() : undefined,
          status: g.status, nights: g.reservations[0].nights,
          roomRate: g.rate !== null ? g.rate : Number(g.reservations[0].room_rate),
          extraCharges: 0, advancePaid: 0, billingType: g.reservations[0].billing_type,
          customerType: 'individual', bulkGroupId: g.groupId,
          notes: `Added to ${g.guestName}'s group after booking — same group rate.`
        });
      }
      onSuccess?.(`Added ${addN} room(s) to ${g.guestName}'s group.`, 'success');
      setAddCount('0'); setAddRoomNumbers('');
      await refreshSelectedGroup(g.groupId);
    } catch (err) {
      onSuccess?.('Error: ' + err.message, 'error');
    }
    setProcessing(false);
  };

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">✏️ Edit Group</span>
        <span className="panel-sub">Change the rate, or add/remove rooms — applies to the whole group at once</span>
      </div>
      <div style={{ padding: '20px 24px' }}>
        {!selectedGroup && (
          <>
            <div className="field" style={{ marginBottom: 16 }}>
              <label>Filter by Group Contact's Phone <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 10 }}>(optional)</span></label>
              <input value={phoneFilter} onChange={e => setPhoneFilter(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Leave blank to see every group" />
            </div>

            {loadingList && <div style={{ padding: '16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Loading...</div>}

            {!loadingList && visibleGroups.length === 0 && (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>No bulk groups found.</div>
            )}

            {!loadingList && visibleGroups.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                {visibleGroups.map(g => (
                  <div key={g.groupId} onClick={() => selectGroup(g)} style={{ cursor: 'pointer', background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{g.guestName} · {g.roomCount} room(s) <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--muted)' }}>({g.status === 'checked-in' ? 'checked in' : 'pending'})</span></div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{g.guestPhone} · {g.checkIn} → {g.checkOut} · {g.rate !== null ? `${fmt(g.rate)}/night each` : '⚠️ Rooms already have different rates'}</div>
                    </div>
                    <button className="btn btn-sm btn-gold">Select →</button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {selectedGroup && (
          <div>
            <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{selectedGroup.guestName} · {selectedGroup.roomCount} room(s) <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--muted)' }}>({isCheckedIn ? 'checked in' : 'pending'})</span></div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                📞 {selectedGroup.guestPhone} · 📅 {selectedGroup.checkIn} → {selectedGroup.checkOut} ({selectedGroup.nights} night{selectedGroup.nights > 1 ? 's' : ''}) · {selectedGroup.billingType === 'gst' ? 'GST Billing' : 'Non-GST'}
              </div>
              {selectedGroup.rate === null && (
                <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>⚠️ These rooms currently have different rates.</div>
              )}
              <button className="btn btn-sm btn-ghost" style={{ marginTop: 8 }} onClick={() => setSelectedGroup(null)}>← Choose a different group</button>
            </div>

            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', marginBottom: 12 }}>Rate — applies to every room</div>
            <div className="field" style={{ marginBottom: 12 }}>
              <label>New Rate / Night (₹) *</label>
              <input type="number" min="0" value={newRate} onChange={e => setNewRate(e.target.value)} />
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>GST and totals recalculate automatically for every room.</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
              <button className="btn btn-gold" onClick={handleConfirm} disabled={processing}>
                {processing ? 'Updating...' : `Apply Rate to ${selectedGroup.roomCount} Room(s) →`}
              </button>
            </div>

            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', marginBottom: 12 }}>Dates — applies to every room</div>
            <div className="form-row-2" style={{ marginBottom: 12 }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Check-in Date</label>
                <input type="date" value={newCheckIn} onChange={e => setNewCheckIn(e.target.value)} />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Check-out Date</label>
                <input type="date" min={newCheckIn} value={newCheckOut} onChange={e => setNewCheckOut(e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
              <button className="btn btn-gold" onClick={handleUpdateDates} disabled={processing}>
                {processing ? 'Updating...' : `Apply Dates to ${selectedGroup.roomCount} Room(s) →`}
              </button>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '0 0 20px' }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', marginBottom: 12 }}>Rooms in This Group</div>
            <div style={{ marginBottom: 20 }}>
              {selectedGroup.reservations.map(r => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{r.room || 'Unassigned'} <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--muted)' }}>({r.status === 'checked-in' ? 'checked in' : 'pending'})</span></div>
                    {editingRoomId === r.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                        <input type="number" min="0" style={{ width: 100, padding: '4px 8px' }} value={editingRoomRate} onChange={e => setEditingRoomRate(e.target.value)} autoFocus />
                        <button className="btn btn-sm btn-gold" style={{ padding: '4px 10px' }} onClick={() => handleSaveRoomRate(r)} disabled={processing}>Save</button>
                        <button className="btn btn-sm btn-ghost" style={{ padding: '4px 10px' }} onClick={() => setEditingRoomId(null)}>Cancel</button>
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        {fmt(r.room_rate)}/night
                        <button className="btn btn-sm btn-ghost" style={{ fontSize: 10, padding: '2px 6px', marginLeft: 6 }} onClick={() => { setEditingRoomId(r.id); setEditingRoomRate(String(r.room_rate)); }}>✏️ Rate</button>
                      </div>
                    )}
                  </div>
                  <button className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)' }} title={r.status === 'checked-in' ? 'Check out to remove' : 'Cancel this room'} onClick={() => handleCancelRoom(r)}>🗑️ Cancel</button>
                </div>
              ))}
            </div>

            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', marginBottom: 12 }}>➕ Add Rooms</div>
            <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
              <div className="form-row-2" style={{ marginBottom: 8 }}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>How Many More</label>
                  <input type="number" min="0" value={addCount} onChange={e => setAddCount(e.target.value)} />
                </div>
                {isCheckedIn && (
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Room Numbers, comma-separated ({addN} needed)</label>
                    <input value={addRoomNumbers} onChange={e => setAddRoomNumbers(e.target.value)} placeholder="e.g. 311,312" />
                  </div>
                )}
              </div>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 10 }}>
                {isCheckedIn
                  ? `Billed at ${selectedGroup.rate !== null ? fmt(selectedGroup.rate) : 'the group'}/night, checked in immediately.`
                  : 'Added as unassigned bookings at the group rate — rooms get picked at actual check-in.'}
              </div>
              <button className="btn btn-sm btn-gold" onClick={handleAddRooms} disabled={processing || addN < 1 || (isCheckedIn && !addRoomsValid)}>
                {processing ? 'Adding...' : `Add ${addN || 0} Room(s)`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
